/**
 * Consentimento de cookies e tecnologias equivalentes.
 *
 * O que este site de fato usa — e a lista é curta de propósito:
 *
 * | O quê          | Tipo             | Para quê                              |
 * |----------------|------------------|---------------------------------------|
 * | `bx_sessao`    | cookie httpOnly  | manter você autenticado               |
 * | `bx_refresh`   | cookie httpOnly  | renovar a sessão sem pedir senha      |
 * | `bx_csrf`      | cookie legível   | impedir que outro site aja por você   |
 * | `bx_visita`    | sessionStorage   | não contar a mesma aba duas vezes     |
 * | `bx_origem`    | localStorage     | de onde veio, enviado só no cadastro  |
 * | `bx_consent`   | localStorage     | lembrar a escolha feita aqui          |
 *
 * Não há cookie de publicidade, de rede social, de remarketing nem de terceiro.
 * Por isso o painel tem duas categorias, e não as cinco de praxe: oferecer
 * botão para desligar o que não existe seria encenação, não transparência.
 *
 * **Necessários** não são opcionais e não pedem consentimento — sem eles não há
 * login nem proteção contra CSRF. A LGPD trata isso como execução do contrato
 * (art. 7º, V), não como escolha do titular.
 *
 * **Medição de audiência** é opcional e pode ser desligada a qualquer momento.
 * Ela roda sob legítimo interesse (art. 7º, IX) porque o dado é anônimo por
 * construção — o servidor guarda um hash de IP + navegador com sal que troca
 * todo dia, nunca o IP. Ainda assim damos o botão: informar sem permitir sair
 * seria informar pela metade.
 */

const CHAVE = 'bx_consent';
/** Canal de aquisição guardado pela medição (ver util/visita.ts). */
const CHAVE_ORIGEM = 'bx_origem';

/** Recusar ou revogar a medição apaga o que ela já tinha guardado. */
function apagarMedicao(): void {
  try {
    localStorage.removeItem(CHAVE_ORIGEM);
  } catch {
    /* nada a fazer */
  }
}

/** Versão do aviso. Subir isto faz o banner reaparecer para todo mundo. */
const VERSAO = 1;

export interface Consentimento {
  versao: number;
  /** Medição de audiência anônima. */
  audiencia: boolean;
  /** ISO da decisão — a LGPD exige poder demonstrar quando foi dada. */
  decididoEm: string;
}

/** Decisão guardada, ou `null` se a pessoa ainda não escolheu. */
export function lerConsentimento(): Consentimento | null {
  try {
    const bruto = localStorage.getItem(CHAVE);
    if (!bruto) return null;
    const c = JSON.parse(bruto) as Consentimento;
    // Aviso mudou desde a decisão: a escolha antiga não vale para o texto novo.
    if (c.versao !== VERSAO) return null;
    return c;
  } catch {
    // Armazenamento bloqueado ou JSON corrompido: trata como quem não decidiu.
    return null;
  }
}

export function gravarConsentimento(audiencia: boolean): Consentimento {
  const c: Consentimento = {
    versao: VERSAO,
    audiencia,
    decididoEm: new Date().toISOString(),
  };
  if (!audiencia) apagarMedicao();
  try {
    localStorage.setItem(CHAVE, JSON.stringify(c));
  } catch {
    // Sem armazenamento, a escolha vale só para esta navegação. Melhor isso do
    // que quebrar a página de quem bloqueia tudo.
  }
  window.dispatchEvent(new CustomEvent('bx:consentimento', { detail: c }));
  return c;
}

/** Apaga a decisão — o banner volta a perguntar. */
export function esquecerConsentimento(): void {
  apagarMedicao();
  try {
    localStorage.removeItem(CHAVE);
  } catch {
    /* nada a fazer */
  }
  window.dispatchEvent(new CustomEvent('bx:consentimento', { detail: null }));
}

/**
 * A medição pode rodar agora?
 *
 * Enquanto a pessoa não decide, a resposta é NÃO. Contar antes da escolha
 * transformaria o banner num enfeite — que é exatamente a prática que a LGPD
 * existe para coibir.
 */
export function podeMedirAudiencia(): boolean {
  return lerConsentimento()?.audiencia === true;
}
